import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { startOfMonth, getDaysInMonth, getDay, format, addMonths, isSameDay, isSameMonth } from 'date-fns';
import { Event, parseLocalDate } from '../lib/supabase';
import EventDetail from './EventDetail';
import './Calendar.css';

interface CalendarProps {
  events: Event[];
  onUpdateEvent: (id: string, updates: Partial<Event>) => Promise<void>;
  onDeleteEvent: (id: string) => Promise<void>;
}

const Calendar: React.FC<CalendarProps> = ({ events, onUpdateEvent, onDeleteEvent }) => {
  const today = new Date();
  const [startMonth, setStartMonth] = useState(startOfMonth(today));
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [monthEventsModal, setMonthEventsModal] = useState<Date | null>(null);
  const [hiddenEvents, setHiddenEvents] = useState<Set<string>>(new Set());
  const [eventColors, setEventColors] = useState<Record<string, string>>({});
  const scrollPositionRef = useRef<number>(0);
  
  // Load hidden events and colors from localStorage
  useEffect(() => {
    const storedHidden = localStorage.getItem('hiddenEvents');
    const storedColors = localStorage.getItem('eventColors');
    if (storedHidden) {
      setHiddenEvents(new Set(JSON.parse(storedHidden)));
    }
    if (storedColors) {
      setEventColors(JSON.parse(storedColors));
    }
  }, []);

  // Save hidden events and colors to localStorage
  useEffect(() => {
    localStorage.setItem('hiddenEvents', JSON.stringify(Array.from(hiddenEvents)));
  }, [hiddenEvents]);

  useEffect(() => {
    localStorage.setItem('eventColors', JSON.stringify(eventColors));
  }, [eventColors]);

  // Update selectedEvent when events array changes (after save)
  useEffect(() => {
    if (selectedEvent?.id) {
      const updatedEvent = events.find(e => e.id === selectedEvent.id);
      if (updatedEvent) {
        setSelectedEvent(updatedEvent);
      }
    }
  }, [events, selectedEvent?.id]);

  const monthsToShow = useMemo(() => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      months.push(addMonths(startMonth, i));
    }
    return months;
  }, [startMonth]);

  const getEventsForDay = (date: Date) => {
    return events.filter(e => {
      if (hiddenEvents.has(e.id || '')) return false;
      const startDate = parseLocalDate(e.start_date);
      startDate.setHours(0, 0, 0, 0);
      const endDate = e.end_date ? parseLocalDate(e.end_date) : startDate;
      endDate.setHours(23, 59, 59, 999);
      
      const dayDate = new Date(date);
      dayDate.setHours(0, 0, 0, 0);
      
      return dayDate >= startDate && dayDate <= endDate;
    });
  };

  const handleStartMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [year, month] = e.target.value.split('-').map(Number);
    setStartMonth(new Date(year, month, 1));
  };

  const getCurrentStartMonthValue = () => {
    return `${startMonth.getFullYear()}-${startMonth.getMonth()}`;
  };

  const getEventsForMonth = (monthDate: Date) => {
    return events.filter(e => {
      const startDate = parseLocalDate(e.start_date);
      return isSameMonth(startDate, monthDate);
    }).sort((a, b) => {
      const dateA = parseLocalDate(a.start_date).getTime();
      const dateB = parseLocalDate(b.start_date).getTime();
      return dateA - dateB;
    });
  };

  const toggleEventVisibility = (eventId: string) => {
    setHiddenEvents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const setEventColor = (eventId: string, color: string) => {
    setEventColors(prev => ({
      ...prev,
      [eventId]: color
    }));
  };

  const getEventColor = (eventId: string | undefined): string => {
    if (!eventId) return '';
    return eventColors[eventId] || '';
  };
  
  const handleEventClick = (event: Event) => {
    // Save current scroll position
    scrollPositionRef.current = window.scrollY || document.documentElement.scrollTop;
    setSelectedEvent(event);
  };
  
  const handleCloseModal = () => {
    setSelectedEvent(null);
    // Restore scroll position after a brief delay to ensure modal is closed
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 100);
  };

  const getAvailableMonths = () => {
    const months = [];
    const currentYear = today.getFullYear();
    for (let year = currentYear; year <= currentYear + 2; year++) {
      for (let month = 0; month < 12; month++) {
        const date = new Date(year, month, 1);
        months.push({
          value: `${year}-${month}`,
          label: format(date, 'MMMM yyyy'),
          date
        });
      }
    }
    return months;
  };

  const renderMonth = (monthDate: Date) => {
    const daysInMonth = getDaysInMonth(monthDate);
    // For December, only show dates 21-31
    const isDecember = monthDate.getMonth() === 11; // December is month 11 (0-indexed)
    const startDay = isDecember ? 21 : 1;
    const days = Array.from({ length: daysInMonth - startDay + 1 }, (_, i) => startDay + i);
    // Calculate the day of week for the first visible day
    const firstVisibleDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), startDay);
    const firstDay = getDay(firstVisibleDate);
    const monthKey = format(monthDate, 'yyyy-MM');
    const monthEvents = getEventsForMonth(monthDate);

    return (
      <div key={monthKey} className="calendar-month-section">
        <div className="calendar-month-header">
          <h3>{format(monthDate, 'MMMM yyyy')}</h3>
          {monthEvents.length > 0 && (
            <button
              className="btn-show-events"
              onClick={() => setMonthEventsModal(monthDate)}
              aria-label="Show all events for this month"
            >
              Show me all events ({monthEvents.length})
            </button>
          )}
        </div>
        <div className="calendar-grid">
          <div className="calendar-header">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="calendar-day-header">{day}</div>
            ))}
          </div>
          <div className="calendar-body">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${monthKey}-${i}`} className="calendar-day empty" />
            ))}
            {days.map(day => {
              const dayDate = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
              const dayEvents = getEventsForDay(dayDate);
              const isToday = isSameDay(dayDate, today);
              
              return (
                <div 
                  key={`${monthKey}-${day}`} 
                  className={`calendar-day ${isToday ? 'today' : ''}`}
                >
                  <div className={`day-number ${isToday ? 'today-number' : ''}`}>{day}</div>
                  <div className="day-events">
                    {dayEvents.slice(0, 3).map(event => {
                      const customColor = getEventColor(event.id);
                      return (
                        <div
                          key={event.id}
                          className="day-event"
                          onClick={() => handleEventClick(event)}
                          style={customColor ? {
                            background: customColor,
                            borderColor: customColor,
                          } : {}}
                        >
                          {event.title}
                        </div>
                      );
                    })}
                    {dayEvents.length > 3 && (
                      <div className="day-event-more">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="calendar">
        <div className="calendar-controls">
          <div className="month-navigation">
            <div className="month-selector">
              <label htmlFor="start-month-select">Start from:</label>
              <select 
                id="start-month-select"
                value={getCurrentStartMonthValue()} 
                onChange={handleStartMonthChange}
                className="month-dropdown"
                aria-label="Select starting month"
              >
                {getAvailableMonths().map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="calendar-months-container">
          {monthsToShow.map(month => renderMonth(month))}
        </div>
      </div>
      
      {selectedEvent && createPortal(
        <EventDetail
          event={selectedEvent}
          onUpdateEvent={onUpdateEvent}
          onDeleteEvent={onDeleteEvent}
          onClose={handleCloseModal}
        />,
        document.body
      )}

      {monthEventsModal && createPortal(
        <MonthEventsModal
          month={monthEventsModal}
          events={getEventsForMonth(monthEventsModal)}
          hiddenEvents={hiddenEvents}
          eventColors={eventColors}
          onToggleVisibility={toggleEventVisibility}
          onSetColor={setEventColor}
          onClose={() => setMonthEventsModal(null)}
        />,
        document.body
      )}
    </>
  );
};

// Month Events Modal Component
interface MonthEventsModalProps {
  month: Date;
  events: Event[];
  hiddenEvents: Set<string>;
  eventColors: Record<string, string>;
  onToggleVisibility: (eventId: string) => void;
  onSetColor: (eventId: string, color: string) => void;
  onClose: () => void;
}

const MonthEventsModal: React.FC<MonthEventsModalProps> = ({
  month,
  events,
  hiddenEvents,
  eventColors,
  onToggleVisibility,
  onSetColor,
  onClose,
}) => {
  const colorOptions = [
    { value: '', label: 'Default' },
    { value: 'linear-gradient(90deg, rgba(14, 165, 255, 0.25), rgba(255, 43, 209, 0.25))', label: 'Blue-Pink' },
    { value: 'rgba(239, 68, 68, 0.25)', label: 'Red' },
    { value: 'rgba(34, 197, 94, 0.25)', label: 'Green' },
    { value: 'rgba(251, 191, 36, 0.25)', label: 'Yellow' },
    { value: 'rgba(168, 85, 247, 0.25)', label: 'Purple' },
    { value: 'rgba(249, 115, 22, 0.25)', label: 'Orange' },
  ];

  return (
    <div className="event-modal-overlay" onClick={onClose}>
      <div className="event-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        <h3>{format(month, 'MMMM yyyy')} - All Events</h3>
        <div className="month-events-list">
          {events.length === 0 ? (
            <p style={{ color: 'var(--muted)', textAlign: 'center', padding: '20px' }}>
              No events for this month
            </p>
          ) : (
            events.map(event => {
              const eventDate = parseLocalDate(event.start_date);
              const isHidden = hiddenEvents.has(event.id || '');
              const currentColor = eventColors[event.id || ''] || '';
              
              return (
                <div key={event.id} className="month-event-item" style={{ opacity: isHidden ? 0.4 : 1 }}>
                  <div className="month-event-date">
                    {format(eventDate, 'MMM d')}
                  </div>
                  <div className="month-event-content">
                    <div className="month-event-title">{event.title}</div>
                    {event.location && (
                      <div className="month-event-meta">{event.location}</div>
                    )}
                  </div>
                  <div className="month-event-actions">
                    <select
                      className="event-color-select"
                      value={currentColor}
                      onChange={(e) => onSetColor(event.id || '', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {colorOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <label className="event-visibility-checkbox">
                      <input
                        type="checkbox"
                        checked={!isHidden}
                        onChange={() => onToggleVisibility(event.id || '')}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={isHidden ? 'Show event on calendar' : 'Hide event from calendar'}
                      />
                      <span>Show on calendar</span>
                    </label>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default Calendar;
